/**
 * Hook for syncing preorder filters with URL query parameters
 *
 * This hook synchronizes Jotai atoms with URL query parameters, enabling:
 * - Page reload persistence
 * - Shareable filter URLs
 * - Browser back/forward navigation
 *
 * Uses the same parameter structure as the API for consistency
 */
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PREORDERS_SERVICE_SORT } from '@ekhodealer/ekho-common';

import type { PreordersServiceSort } from '@ekhodealer/ekho-common';
import type { SortDirection } from '@ekhodealer/ekho-common/rev';

import { CommonDateFilter as OrderDateFilter } from '@common/types/filters';
import { parseArray, parseNumber } from '@common/utils/parse';

import {
	currentPageAtom,
	debouncedSearchValueAtom,
	estRetailPriceRangeAtom,
	itemsPerPageAtom,
	modelFilterAtom,
	orderDateCustomRangeAtom,
	orderDateFilterAtom,
	preorderAmountRangeAtom,
	registrationStateFilterAtom,
	sortConfigAtom,
	statusFilterAtom,
} from '../atoms/preordersAtoms';

/**
 * Map sort parameter to column and direction
 * Converts PreordersServiceSort enum values to UI sort state
 */
const mapSortParamToConfig = (
	sortParam: string,
): { column: string; direction: SortDirection } | null => {
	const sortMap: Record<string, { column: string; direction: SortDirection }> = {
		[PREORDERS_SERVICE_SORT.DATE_PURCHASED_ASC]: {
			column: 'datePurchased',
			direction: 'ascending',
		},
		[PREORDERS_SERVICE_SORT.DATE_PURCHASED_DESC]: {
			column: 'datePurchased',
			direction: 'descending',
		},
		[PREORDERS_SERVICE_SORT.BUYER_ASC]: { column: 'buyer', direction: 'ascending' },
		[PREORDERS_SERVICE_SORT.BUYER_DESC]: { column: 'buyer', direction: 'descending' },
		[PREORDERS_SERVICE_SORT.ORDER_FEE_ASC]: {
			column: 'preorderAmount',
			direction: 'ascending',
		},
		[PREORDERS_SERVICE_SORT.ORDER_FEE_DESC]: {
			column: 'preorderAmount',
			direction: 'descending',
		},
	};

	return sortMap[sortParam] || null;
};

/**
 * Map column and direction to sort parameter
 * Converts UI sort state to PreordersServiceSort enum value
 */
const mapConfigToSortParam = (column: string, direction: SortDirection): string | null => {
	if (direction === 'none') return null;

	const sortMap: Record<string, { asc: PreordersServiceSort; desc: PreordersServiceSort }> = {
		datePurchased: {
			asc: PREORDERS_SERVICE_SORT.DATE_PURCHASED_ASC,
			desc: PREORDERS_SERVICE_SORT.DATE_PURCHASED_DESC,
		},
		buyer: {
			asc: PREORDERS_SERVICE_SORT.BUYER_ASC,
			desc: PREORDERS_SERVICE_SORT.BUYER_DESC,
		},
		preorderAmount: {
			asc: PREORDERS_SERVICE_SORT.ORDER_FEE_ASC,
			desc: PREORDERS_SERVICE_SORT.ORDER_FEE_DESC,
		},
	};

	const columnSort = sortMap[column];
	if (!columnSort) return null;

	return direction === 'ascending' ? columnSort.asc : columnSort.desc;
};

/**
 * Hook to synchronize preorder filters with URL query parameters
 */
export const usePreordersFiltersUrl = (): void => {
	const [searchParams, setSearchParams] = useSearchParams();
	const isInitialized = useRef(false);
	const isSyncingFromUrl = useRef(false);

	// Setters for all filter atoms
	const setSearchValue = useSetAtom(debouncedSearchValueAtom);
	const setStatuses = useSetAtom(statusFilterAtom);
	const setModels = useSetAtom(modelFilterAtom);
	const setOrderDate = useSetAtom(orderDateFilterAtom);
	const setOrderDateCustomRange = useSetAtom(orderDateCustomRangeAtom);
	const setPreorderAmountRange = useSetAtom(preorderAmountRangeAtom);
	const setEstRetailPriceRange = useSetAtom(estRetailPriceRangeAtom);
	const setRegistrationState = useSetAtom(registrationStateFilterAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);
	const setItemsPerPage = useSetAtom(itemsPerPageAtom);
	const setSortConfig = useSetAtom(sortConfigAtom);

	// Getters for all filter atoms (to sync to URL)
	const searchValue = useAtomValue(debouncedSearchValueAtom);
	const statuses = useAtomValue(statusFilterAtom);
	const models = useAtomValue(modelFilterAtom);
	const orderDate = useAtomValue(orderDateFilterAtom);
	const orderDateCustomRange = useAtomValue(orderDateCustomRangeAtom);
	const preorderAmountRange = useAtomValue(preorderAmountRangeAtom);
	const estRetailPriceRange = useAtomValue(estRetailPriceRangeAtom);
	const registrationState = useAtomValue(registrationStateFilterAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const sortConfig = useAtomValue(sortConfigAtom);

	// Initialize atoms from URL on mount
	useEffect(() => {
		if (isInitialized.current) return;

		isSyncingFromUrl.current = true;

		// Parse URL params and set atoms
		const urlQuery = searchParams.get('query');
		if (urlQuery) {
			setSearchValue(urlQuery);
		}

		// Note: Tab is managed through route path, not query parameters

		const urlStatuses = searchParams.get('statuses');
		if (urlStatuses) {
			setStatuses(parseArray(urlStatuses));
		}

		const urlModels = searchParams.get('models');
		if (urlModels) {
			setModels(parseArray(urlModels));
		}

		const urlOrderDate = searchParams.get('orderDate');
		if (
			urlOrderDate &&
			Object.values(OrderDateFilter).includes(urlOrderDate as OrderDateFilter)
		) {
			setOrderDate(urlOrderDate as OrderDateFilter);
		}

		// Parse custom order date range
		const urlOrderDateFrom = searchParams.get('orderDateFrom');
		const urlOrderDateTo = searchParams.get('orderDateTo');
		if (urlOrderDateFrom || urlOrderDateTo) {
			setOrderDateCustomRange({
				from: urlOrderDateFrom ? parseNumber(urlOrderDateFrom, 0) || null : null,
				to: urlOrderDateTo ? parseNumber(urlOrderDateTo, 0) || null : null,
			});
		}

		// Parse preorder amount range
		const urlAmountMin = searchParams.get('amountMin');
		const urlAmountMax = searchParams.get('amountMax');
		if (urlAmountMin || urlAmountMax) {
			setPreorderAmountRange({
				...(urlAmountMin ? { min: parseNumber(urlAmountMin, 0) } : {}),
				...(urlAmountMax ? { max: parseNumber(urlAmountMax, 0) } : {}),
			});
		}

		// Parse estimated retail price range
		const urlPriceMin = searchParams.get('priceMin');
		const urlPriceMax = searchParams.get('priceMax');
		if (urlPriceMin || urlPriceMax) {
			setEstRetailPriceRange({
				...(urlPriceMin ? { min: parseNumber(urlPriceMin, 0) } : {}),
				...(urlPriceMax ? { max: parseNumber(urlPriceMax, 0) } : {}),
			});
		}

		const urlState = searchParams.get('state');
		if (urlState) {
			setRegistrationState(urlState);
		}

		const urlPage = searchParams.get('page');
		if (urlPage) {
			setCurrentPage(parseNumber(urlPage, 1));
		}

		const urlItemsPerPage = searchParams.get('itemsPerPage');
		if (urlItemsPerPage) {
			setItemsPerPage(parseNumber(urlItemsPerPage, 50));
		}

		const urlSort = searchParams.get('sortBy');
		if (urlSort) {
			const sortConfigFromUrl = mapSortParamToConfig(urlSort);
			if (sortConfigFromUrl) {
				setSortConfig([sortConfigFromUrl]);
			}
		}

		isInitialized.current = true;
		isSyncingFromUrl.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount, setters are stable
	}, []); // Only run on mount - setters are stable and don't need to be in deps

	// Sync atoms to URL whenever they change
	useEffect(() => {
		// Skip if we're currently syncing from URL to avoid circular updates
		if (!isInitialized.current || isSyncingFromUrl.current) return;

		const params = new URLSearchParams();

		// Add query parameter
		if (searchValue) params.set('query', searchValue);

		// Note: Tab is managed through route path, not query parameters

		// Add status filters
		if (statuses.length > 0) {
			params.set('statuses', statuses.join(','));
		}

		// Add model filters
		if (models.length > 0) {
			params.set('models', models.join(','));
		}

		// Add order date filter
		if (orderDate) {
			params.set('orderDate', orderDate);
		}

		// Add custom order date range
		if (orderDate === OrderDateFilter.CUSTOM) {
			if (orderDateCustomRange.from !== null) {
				params.set('orderDateFrom', orderDateCustomRange.from.toString());
			}
			if (orderDateCustomRange.to !== null) {
				params.set('orderDateTo', orderDateCustomRange.to.toString());
			}
		}

		// Add preorder amount range
		if (preorderAmountRange.min !== undefined) {
			params.set('amountMin', preorderAmountRange.min.toString());
		}
		if (preorderAmountRange.max !== undefined) {
			params.set('amountMax', preorderAmountRange.max.toString());
		}

		// Add estimated retail price range
		if (estRetailPriceRange.min !== undefined) {
			params.set('priceMin', estRetailPriceRange.min.toString());
		}
		if (estRetailPriceRange.max !== undefined) {
			params.set('priceMax', estRetailPriceRange.max.toString());
		}

		// Add registration state filter
		if (registrationState) {
			params.set('state', registrationState);
		}

		// Add page parameter (only if not 1)
		if (currentPage !== 1) {
			params.set('page', currentPage.toString());
		}

		// Add items per page parameter (only if not default 50)
		if (itemsPerPage !== 50) {
			params.set('itemsPerPage', itemsPerPage.toString());
		}

		// Add sort parameter
		if (sortConfig.length > 0) {
			const sort = sortConfig[0];
			if (sort) {
				const sortParam = mapConfigToSortParam(sort.column, sort.direction);
				if (sortParam) {
					params.set('sortBy', sortParam);
				}
			}
		}

		// Update URL without adding to history (use replace to avoid cluttering browser history)
		setSearchParams(params, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync URL when filter atoms change, setSearchParams is stable
	}, [
		searchValue,
		statuses,
		models,
		orderDate,
		orderDateCustomRange,
		preorderAmountRange,
		estRetailPriceRange,
		registrationState,
		currentPage,
		itemsPerPage,
		sortConfig,
	]);
};
